import { type StorySlice, type TextStory, distributeStory } from '@/lib/text-linking/chain-story'
import type { ChainStoryFrame } from '../types/chain-story-frame'

/**
 * Splits stories across a boundary separating two runs of frames: the upstream run keeps what fits,
 * and — for a gesture that keeps the two sides connected — its remainder leads the downstream run's
 * own content.
 *
 * The single primitive behind both gestures that cut or join a story, and the reason neither needs a
 * provenance marker of its own:
 *
 * - **link** (D3, forward-only): `upstreamStory` is the SOURCE side's story alone, so the target
 *   keeps its own text and only the source's overset pushes forward into it. Nothing is pulled up.
 *   This is what the old reflow's `separateStoryAfterFrameId` marker used to approximate.
 *   `carryUpstreamOverflowForward` stays at its default (`true`) here — the overset pushing forward
 *   into the target is the whole point of linking two frames.
 * - **unlink** (D8, WMPCM-10398): the chain was ONE story until the cut, so `upstreamStory` is the
 *   whole pre-unlink story and `downstreamStory` is empty. The upstream side keeps as much as its
 *   OWN frames can hold — with `carryUpstreamOverflowForward: false` — rather than handing what does
 *   not fit to the tail it was just severed from: the tail is a freshly separate story now, not a
 *   destination for the old one's overset, so it starts empty (shown via its own placeholder state)
 *   and the upstream side shows whatever's left as ordinary overset (its own overflow indicator),
 *   exactly like any other frame holding more than it can display.
 *
 * `tailOverflowsPastLastFrame` is what makes carrying the overflow forward possible at all: without
 * it the last upstream frame absorbs everything unclipped and there is no remainder to hand across.
 * `carryUpstreamOverflowForward: false` skips it for precisely that reason — the last upstream frame
 * should absorb everything, unclipped, exactly like a solo frame with nowhere to send overset.
 *
 * Returns the window's seams alongside its slices so the caller can record them
 * (`recordChainSeams`). The cut itself is a boundary neither sub-distribution reports —
 * `distributeStory`'s `seams` only covers boundaries between the frames it was given — so when the
 * overflow does carry forward it comes from `remainderIsFragment`, at index `upstream.length - 1` of
 * the combined array; when it does not (unlink), the caller overwrites that entry with `undefined`
 * anyway, since the cut boundary is retired rather than recorded.
 */
export function distributeAcrossChainBoundary(
  upstream: readonly ChainStoryFrame[],
  upstreamStory: TextStory,
  downstream: readonly ChainStoryFrame[],
  downstreamStory: TextStory,
  options?: { carryUpstreamOverflowForward?: boolean }
): { slices: StorySlice[]; seams: (boolean | undefined)[] } {
  const carryForward = options?.carryUpstreamOverflowForward ?? true
  const up = distributeStory(
    upstreamStory,
    upstream,
    carryForward ? { tailOverflowsPastLastFrame: true } : undefined
  )
  const down = distributeStory(
    [...(carryForward ? up.remainder : []), ...downstreamStory],
    downstream
  )
  // Only a real cut has a boundary to describe: with either side empty the combined window is just
  // the other side, and an extra entry would shift every seam after it by one.
  const cutSeam = upstream.length > 0 && downstream.length > 0 ? [up.remainderIsFragment] : []

  return {
    slices: [...up.slices, ...down.slices],
    seams: [...up.seams, ...cutSeam, ...down.seams],
  }
}
